import { ApexOptions } from "apexcharts"
import { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import { colorMapper, networkLabel } from "src/shared/enums/networkColors"
import { monthDayLabels } from "../../../chartDateAxis"

/*
    Reach By Network (MPN-1530 §3.6).

    Line-and-dot trend with the legend below, matching the standalone Meta Reach report
    the ticket points at.

    Plotted as a PERCENTAGE of the audience we selected, not a headcount (MPN-1548).
    "412,000 reached" answers nothing on its own: whether that is good depends entirely
    on how many people we were trying to reach, and Ops was reading it against campaigns
    of wildly different sizes. The share makes two campaigns comparable and makes one
    campaign's own trend mean something.

    The denominator is Selected, the count we actually uploaded for this audience, not
    Available or Matched. Available includes people no network was ever asked about, and
    Matched is the network's own answer to the same question reach is measuring -- using
    it would divide a number by a version of itself. Selected is the ask; reach is the
    answer.

    Meta only, and that is a data fact rather than a scoping choice. `reach` is a real
    column on the performance record, but only the Meta adapter ever writes it: Google,
    Pinterest, Snapchat, LinkedIn and Vibe all leave it null (see each network's
    performance_command under content_network/publish). Drawing them would be six series,
    four of them flat on zero, implying those networks reached nobody rather than that
    nobody asked them.

    So a network is charted only if it actually reported reach at some point in the
    window, and the ones that cannot are named underneath instead of silently missing.
    When Google or Pinterest reach gets wired up, their series appear here with no change
    to this file.
*/

// The networks whose adapters populate reach today.
const REACH_CAPABLE = new Set(["facebook", "instagram"])

const num = (v: unknown) => {
  const n = typeof v === "number" ? v : Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

export default function ReachByNetwork({ data }: { data: any }) {
  const { series, options, missing, selected } = useMemo(() => {
    const rows: any[] = data?.["performance-by-day"] ?? []

    /*
        Selected for whatever the page is scoped to. When an audience is picked the
        payload carries that one; otherwise "all" is the campaign-wide figure. Both
        come from audience-breakdown, already in this response for the Audience tab,
        so the percentage costs no extra query.
    */
    const breakdown = data?.["audience-breakdown"]
    const scoped = breakdown?.all ?? breakdown?.audiences?.[0]
    const selected = num(scoped?.selected)

    const days: string[] = []
    const seen = new Set<string>()
    const byNetwork = new Map<string, Map<string, number>>()
    const networksPresent = new Set<string>()

    for (const row of rows) {
      const day = row?.day_start
      const network = row?.reporting_network
      if (!day || !network) continue
      if (!seen.has(day)) {
        seen.add(day)
        days.push(day)
      }
      networksPresent.add(network)
      const perDay = byNetwork.get(network) ?? new Map<string, number>()
      perDay.set(day, (perDay.get(day) ?? 0) + num(row.reach_total))
      byNetwork.set(network, perDay)
    }
    days.sort()

    // Only networks that actually reported reach. A network present in the data but
    // reporting zero every day has not been reached-measured, it has been left null and
    // summed to zero by the query's COALESCE.
    const charted = [...byNetwork.entries()].filter(([, perDay]) =>
      [...perDay.values()].some((v) => v > 0),
    )

    /*
        A share, capped at nothing. Reach can exceed Selected -- a network counts the
        people it showed the ad to, which includes lookalike and broad delivery beyond
        the list we handed it -- so the line can legitimately pass 100%. Clamping would
        hide over-delivery, which is a thing Ops wants to see, not a rendering error.
    */
    const share = (reach: number) => (selected > 0 ? (reach / selected) * 100 : 0)

    const series = charted.map(([network, perDay]) => ({
      name: networkLabel(network),
      data: days.map((d) => share(perDay.get(d) ?? 0)),
    }))

    const missing = [...networksPresent]
      .filter((n) => !REACH_CAPABLE.has(n))
      .map((n) => networkLabel(n))

    const options: ApexOptions = {
      chart: {
        type: "line",
        height: 300,
        toolbar: { show: false },
        animations: { enabled: false },
        zoom: { enabled: false },
      },
      // Keyed off the series name, like every other by-network chart here. A flat
      // array would colour by position, so Facebook and Instagram only look right
      // while they are the only two reporting and arrive in that order.
      colors: colorMapper(series),
      stroke: { width: 2, curve: "smooth" },
      // The dots the reference report uses. Small, so a 75-day window stays readable.
      markers: { size: 3, strokeWidth: 0, hover: { size: 5 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f1f1f1" },
      xaxis: { categories: days, tickAmount: 8, labels: monthDayLabels },
      yaxis: {
        // Anchored at 0 but not capped at 100, for the over-delivery case above. The
        // reference report's 0/50/100 gridlines fall out of forceNiceScale on the
        // ordinary range without pinning the axis when a series runs past it.
        min: 0,
        forceNiceScale: true,
        labels: { formatter: (v: number) => `${Math.round(v ?? 0)}%` },
        title: { text: "% of selected audience reached" },
      },
      legend: { position: "bottom" },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (v: number) => `${(v ?? 0).toFixed(1)}% of selected` },
      },
    }

    return { series, options, missing, selected }
  }, [data])

  /*
      No denominator means no percentage. Saying so beats drawing every network flat on
      zero, which reads as "nobody was reached" when it means "we do not know what we
      were aiming at".
  */
  if (selected <= 0) {
    return (
      <p className="text-muted fs-11 mb-0">
        No selected audience count for this campaign, so reach cannot be shown as a
        share. It appears once an audience has been selected.
      </p>
    )
  }

  if (series.length === 0) {
    return (
      <p className="text-muted fs-11 mb-0">
        No reach reported in this date range. Reach is currently available from Meta
        (Facebook and Instagram) only.
      </p>
    )
  }

  return (
    <div>
      <ReactApexChart options={options} series={series} type="line" height={300} />
      {missing.length > 0 && (
        <p className="mon-reach__note">
          {missing.join(", ")} {missing.length === 1 ? "does" : "do"} not report reach yet,
          so {missing.length === 1 ? "it is" : "they are"} not charted here.
        </p>
      )}
    </div>
  )
}
